import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../../api/client';
import { DataGrid, type Column } from '../../components/ui/DataGrid';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import AuditLog from './AuditLog';
import { inputClass } from '../../components/ui/Input';

interface FailedEvent {
  id: string;
  event_type: string;
  payload: unknown;
  error: string;
  retry_count: number;
  status: string;
  created_at: string;
  last_retry_at: string | null;
}

/** Failed integration-event admin (Section 27.13) — kept as the second tab. */
function FailedEvents() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [status, setStatus] = useState<'PENDING' | 'RETRYING' | 'DEAD_LETTER' | 'RESOLVED'>(
    'PENDING'
  );
  const [rows, setRows] = useState<FailedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ data: FailedEvent[] } | FailedEvent[]>(
        `/admin/failed-events?status=${status}`
      );
      const list = Array.isArray(data) ? data : data.data;
      setRows(list ?? []);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const retryAll = async () => {
    try {
      const { data } = await apiClient.post<{
        data: { retried: number; succeeded: number; deadLettered: number };
      }>(
        '/admin/failed-events/retry-due',
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      const r = data.data;
      push({
        tone: 'success',
        message: t('admin.audit.retriedSummary', {
          retried: r.retried,
          succeeded: r.succeeded,
          dead: r.deadLettered,
        }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  const columns: Column<FailedEvent>[] = [
    { key: 'event_type', header: t('admin.audit.event') },
    { key: 'status', header: t('admin.audit.status') },
    { key: 'retry_count', header: t('admin.audit.retries') },
    { key: 'error', header: t('admin.audit.error') },
    { key: 'created_at', header: t('admin.audit.created') },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className={inputClass + ' h-8 w-auto'}
        >
          {(['PENDING', 'RETRYING', 'DEAD_LETTER', 'RESOLVED'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`admin.audit.statuses.${s}`)}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={retryAll}>
          <RefreshCw className="h-4 w-4" />
          {t('admin.audit.retryDue')}
        </Button>
      </div>
      <DataGrid columns={columns} rows={rows} loading={loading} rowKey={(r) => r.id} />
    </div>
  );
}

type Tab = 'history' | 'events';

/** Screen 44 — Audit logs. Change history (append-only audit_logs) + failed-events admin. */
export default function Audit() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('history');
  // Deep-link support: /admin/audit?entity=admin_settings lands on change history, pre-filtered.
  const entityFilter = searchParams.get('entity') ?? undefined;

  const select = (t: Tab): void => {
    setTab(t);
    if (t !== 'history') {
      searchParams.delete('entity');
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div>
      <PageHeader title={t('admin.audit.title')} description={t('admin.audit.description')} />
      <div className="mb-6 flex gap-1 border-b">
        {(
          [
            ['history', t('admin.audit.changeHistory')],
            ['events', t('admin.audit.failedEvents')],
          ] as [Tab, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => select(id)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'history' ? <AuditLog defaultEntity={entityFilter} /> : <FailedEvents />}
    </div>
  );
}
